import React, { useState, useEffect, useRef, useMemo, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SwipeConfig } from '../types';
import { SwipeableViewProps } from '../types/swipeable';
import { useSwipeNavigation } from '../hooks/useSwipeNavigation';
import { PageContent } from './swipeable/PageContent';
import { DPadControls } from './swipeable/DPadControls';
import { CompanyModal } from './swipeable/CompanyModal';
import { AnalyticsTrackingService } from '../services/analyticsTrackingService';
import { getGlobalSlideState } from './swipeable/PartialHorizontalSlide';
import { DomainService } from '../services/domainService';

// 固定位置CTAボタンコンポーネント
const FixedCTAButton: React.FC<{ 
  image?: string;
  hoverImage?: string; // ホバー時の画像
  bottomPercent?: number; // 下からの表示位置（%）
  leftPercent?: number; // 左からの表示位置（%、後方互換性のため残す）
  leftPosition?: 'center' | 'left' | 'right' | 'custom'; // 左右の位置（選択式）
  leftPercentCustom?: number; // カスタム位置の場合の%
  widthPercent?: number; // 横幅（%）
  animationType?: 'none' | 'bounce' | 'float' | 'pulse'; // アニメーションタイプ
  onClick?: () => void 
}> = ({ image, hoverImage, bottomPercent, leftPercent, leftPosition, leftPercentCustom, widthPercent = 0, animationType = 'none', onClick }) => {
  // 位置計算
  const calculateLeftPosition = () => {
    // 新しい方式（leftPosition）が設定されている場合
    if (leftPosition) {
      switch (leftPosition) {
        case 'center':
          return { left: '50%', transform: 'translateX(-50%)' };
        case 'left':
          return { left: '0', transform: 'none' };
        case 'right':
          return { left: '100%', transform: 'translateX(-100%)' };
        case 'custom':
          return { left: `${leftPercentCustom ?? 0}%`, transform: 'none' };
      }
    }
    // 後方互換性：leftPercentが設定されている場合（中央基準）
    if (leftPercent !== undefined) {
      return { left: `${leftPercent}%`, transform: 'translateX(-50%)' };
    }
    // デフォルト（中央）
    return { left: '50%', transform: 'translateX(-50%)' };
  };
  
  const positionStyle = calculateLeftPosition();
  
  // アニメーションクラス名を取得
  const getAnimationClass = () => {
    if (!animationType || animationType === 'none') return '';
    return `qwipe-cta-animation-${animationType}`;
  };
  
  if (image) {
    // 画像がある場合は画像を表示
    return (
      <div
        className="qwipe-global-cta-button"
        style={{
          zIndex: 1000,
        }}
      >
        {/* 外側のdiv: 位置指定のみ */}
        <div
          style={{
            position: 'absolute',
            bottom: `${bottomPercent}%`,
            left: positionStyle.left,
            transform: positionStyle.transform,
            width: `${widthPercent}%`,
            pointerEvents: 'none',
          }}
        >
          {/* 内側のdiv: アニメーションのみ */}
          <div className={getAnimationClass()} style={{ pointerEvents: 'none' }}>
            <img
              src={image}
              alt="CTAボタン"
              loading="lazy"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
                objectFit: 'cover',
                borderRadius: '8px',
                cursor: 'pointer',
                pointerEvents: 'auto',
                transition: 'opacity 0.3s ease',
              }}
              onClick={onClick}
              onMouseEnter={(e) => {
                if (hoverImage) {
                  e.currentTarget.src = hoverImage;
                }
              }}
              onMouseLeave={(e) => {
                if (hoverImage) {
                  e.currentTarget.src = image;
                }
              }}
            />
          </div>
        </div>
      </div>
    );
  } else {
    // 画像がない場合はデフォルトボタンを表示
    const buttonStyle = {
      width: '100%',
      height: '100%',
      padding: '16px 24px',
      backgroundColor: '#1976d2',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: 'bold',
      cursor: 'pointer',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      transition: 'all 0.3s ease',
    };

    return (
      <div
        className="qwipe-global-cta-button"
        style={{
          zIndex: 1000,
        }}
      >
        {/* 外側のdiv: 位置指定のみ */}
        <div
          style={{
            position: 'absolute',
            bottom: `${bottomPercent}%`,
            left: positionStyle.left,
            transform: positionStyle.transform,
            width: `${widthPercent}%`,
            pointerEvents: 'none',
          }}
        >
          {/* 内側のdiv: アニメーションのみ */}
          <div className={getAnimationClass()} style={{ pointerEvents: 'none' }}>
            <button
              style={{
                ...buttonStyle,
                width: '100%',
                pointerEvents: 'auto',
              }}
              onClick={onClick}
            >
              お問い合わせはこちら
            </button>
          </div>
        </div>
      </div>
    );
  }
};

const defaultSwipeConfig: SwipeConfig = {
  threshold: 50,
  enabledDirections: ['up', 'down', 'left', 'right']
};

export const SwipeableView: React.FC<SwipeableViewProps> = ({
  pages,
  site,
  onPageChange,
  onReady,
  swipeConfig = defaultSwipeConfig,
  className = '',
  isPreviewMode = false
}) => {
  // 現在のページIDを管理
  const [currentPageId, setCurrentPageId] = useState(pages[0]?.id || '');
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState<Set<string>>(new Set());
  const [isFirstPageImageReady, setIsFirstPageImageReady] = useState(false);
  
  // デモモードの状態管理
  const [demoMode, setDemoMode] = useState(false);
  const demoTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [demoTrigger, setDemoTrigger] = useState(0); // 部分スワイプ後にデモを再開するためのトリガー

  // 1ページ目のすべてのリソース（画像、CTAボタン、動画、HTML内の画像など）を優先的に読み込む
  // 読み込み完了後に2ページ目以降の読み込みを開始
  useEffect(() => {
    // pagesが空の場合はスキップ
    if (!pages || pages.length === 0) {
      setIsFirstPageImageReady(true);
      if (onReady) {
        onReady();
      }
      return;
    }

    const firstPage = pages[0];
    setIsFirstPageImageReady(false);
    
    // 1ページ目に表示するすべてのリソースを収集
    const resourcesToLoad: string[] = [];
    
    // 1. 背景画像
    if (firstPage?.backgroundImage) {
      resourcesToLoad.push(firstPage.backgroundImage);
    }
    
    // 2. CTAボタン画像（固定位置）
    if (site?.settings?.ctaButtons) {
      const ctaButtons = site.settings.ctaButtons;
      // 新しいデータ構造（ctaButtonIds）
      if (firstPage?.ctaButtonIds && firstPage.ctaButtonIds.length > 0) {
        firstPage.ctaButtonIds.forEach(buttonId => {
          const button = ctaButtons.find(btn => btn.id === buttonId);
          if (button?.image) {
            resourcesToLoad.push(button.image);
          }
          if (button?.hoverImage) {
            resourcesToLoad.push(button.hoverImage);
          }
        });
      }
      // 後方互換性：ctaButtonId
      else if (firstPage?.ctaButtonId) {
        const button = ctaButtons.find(btn => btn.id === firstPage.ctaButtonId);
        if (button?.image) {
          resourcesToLoad.push(button.image);
        }
        if (button?.hoverImage) {
          resourcesToLoad.push(button.hoverImage);
        }
      }
    }
    
    // 3. 埋め込み式CTAボタン画像
    if (site?.settings?.ctaButtons) {
      const ctaButtons = site.settings.ctaButtons;
      let embeddedButtonIds: string[] = [];
      
      // 新しいデータ構造（ctaButtonIds）
      if (firstPage?.ctaButtonIds && firstPage.ctaButtonIds.length > 0) {
        embeddedButtonIds = firstPage.ctaButtonIds;
      }
      // 後方互換性：ctaButtonId
      else if (firstPage?.ctaButtonId) {
        embeddedButtonIds = [firstPage.ctaButtonId];
      }
      
      // 埋め込み式ボタンの画像を読み込む
      embeddedButtonIds.forEach(buttonId => {
        const button = ctaButtons.find(btn => btn.id === buttonId);
        if (button?.displayMode === 'embedded') {
          if (button.image) {
            resourcesToLoad.push(button.image);
          }
          if (button.hoverImage) {
            resourcesToLoad.push(button.hoverImage);
          }
        }
      });
    }
    
    // 4. customHtml内の画像を抽出
    if (firstPage?.customHtml) {
      // <img src="..."> タグから画像URLを抽出
      const imgMatches = firstPage.customHtml.match(/<img[^>]+src=["']([^"']+)["']/gi);
      if (imgMatches) {
        imgMatches.forEach(match => {
          const srcMatch = match.match(/src=["']([^"']+)["']/i);
          if (srcMatch && srcMatch[1]) {
            resourcesToLoad.push(srcMatch[1]);
          }
        });
      }
      // background-image: url(...) から画像URLを抽出
      const bgImageMatches = firstPage.customHtml.match(/background-image:\s*url\(["']?([^"')]+)["']?\)/gi);
      if (bgImageMatches) {
        bgImageMatches.forEach(match => {
          const urlMatch = match.match(/url\(["']?([^"')]+)["']?\)/i);
          if (urlMatch && urlMatch[1]) {
            resourcesToLoad.push(urlMatch[1]);
          }
        });
      }
    }
    
    // 5. 動画（1ページ目のみ）
    if (firstPage?.videoUrl) {
      // 動画は読み込み完了を待たない（動画の読み込みは時間がかかるため）
      // ただし、優先的に読み込む
      const video = document.createElement('video');
      video.preload = 'auto';
      (video as any).fetchPriority = 'high';
      video.src = firstPage.videoUrl;
    }
    
    // 6. horizontalSlidesの画像（部分スワイプがある場合）
    if (firstPage?.horizontalSlides && firstPage.horizontalSlides.length > 0) {
      firstPage.horizontalSlides.forEach(slide => {
        if (slide.backgroundImage) {
          resourcesToLoad.push(slide.backgroundImage);
        }
      });
    }
    
    // 重複を除去
    const uniqueResources = Array.from(new Set(resourcesToLoad));
    
    // リソースがない場合はすぐに表示
    if (uniqueResources.length === 0) {
      setIsFirstPageImageReady(true);
      if (onReady) {
        onReady();
      }
      return;
    }
    
    // すべてのリソースを優先的に読み込む
    const loadPromises = uniqueResources.map(resourceUrl => {
      return new Promise<string>((resolve) => {
        const img = new Image();
        (img as any).fetchPriority = 'high';
        img.onload = () => {
          setImagesLoaded(prev => new Set([...Array.from(prev), resourceUrl]));
          resolve(resourceUrl);
        };
        img.onerror = () => {
          // エラーでも続行
          setImagesLoaded(prev => new Set([...Array.from(prev), resourceUrl]));
          resolve(resourceUrl);
        };
        img.src = resourceUrl;
      });
    });
    
    // すべてのリソースの読み込み完了を待つ
    Promise.all(loadPromises).then(() => {
      setIsFirstPageImageReady(true);
      if (onReady) {
        onReady();
      }
    });

    // タイムアウトを設定（5秒後に強制的に表示）
    const timeout = setTimeout(() => {
      console.warn('SwipeableView: 1ページ目のリソース読み込みタイムアウト - 強制的に表示');
      setIsFirstPageImageReady(true);
      if (onReady) {
        onReady();
      }
    }, 5000);

    return () => clearTimeout(timeout);
  }, [pages, site, onReady]);

  // 隣接ページのCTAボタン画像のみを事前読み込み
  useEffect(() => {
    const preloadAdjacentPageCTAImages = () => {
      const currentPage = pages.find(p => p.id === currentPageId);
      if (!currentPage || !site?.settings?.ctaButtons) return;

      // 隣接ページを特定（上下左右のページ）
      const currentRow = currentPage.row;
      const currentColumn = getColumnNumber(currentPage.column);
      
      const adjacentPages = pages.filter(page => {
        const pageRow = page.row;
        const pageColumn = getColumnNumber(page.column);
        
        // 上下左右の隣接ページを判定
        return (
          (pageRow === currentRow - 1 && pageColumn === currentColumn) || // 上
          (pageRow === currentRow + 1 && pageColumn === currentColumn) || // 下
          (pageRow === currentRow && pageColumn === currentColumn - 1) || // 左
          (pageRow === currentRow && pageColumn === currentColumn + 1)    // 右
        );
      });

      // 隣接ページのCTAボタン画像を読み込み
      adjacentPages.forEach(page => {
        if (page.ctaButtonId && site?.settings?.ctaButtons) {
          const button = site.settings.ctaButtons.find(btn => btn.id === page.ctaButtonId);
          if (button) {
            if (button.image) {
              const img = new Image();
              img.src = button.image;
              img.loading = 'eager';
            }
            if (button.hoverImage) {
              const hoverImg = new Image();
              hoverImg.src = button.hoverImage;
              hoverImg.loading = 'eager';
            }
          }
        }
      });
    };

    preloadAdjacentPageCTAImages();
  }, [currentPageId, pages, site]);

  // スワイプナビゲーションのロジックをカスタムフックから取得
  const {
    handlers,
    currentPage,
    getCurrentPageWithAdjacents,
    getAdjacentPages,
    getColumnNumber,
    isPageChanging,
    swipeProgress,
    isSwipeActive,
    swipeOffset,
    swipeDirection,
    goToNextRow,
    goToPreviousRow,
    goToNextColumn,
    goToPreviousColumn,
    horizontalPageIndex
  } = useSwipeNavigation({
    pages,
    currentPageId,
    setCurrentPageId,
    onPageChange
  });

  // アナリティクストラッキングの初期化
  useEffect(() => {
    const initializeTracking = async () => {
      // プレビューモードの場合はトラッキングをスキップ（最優先チェック）
      const hostname = window.location.hostname;
      const isPreviewByHostname = hostname === 'preview.qwipe.jp' || hostname === 'www.preview.qwipe.jp' || hostname.includes('preview.qwipe.jp');
      
      if (isPreviewMode || isPreviewByHostname) {
        return;
      }

      if (!site?.id || !pages || pages.length === 0) {
        return;
      }

      // 有効なドメイン設定があるかチェック
      try {
        const domainConfig = await DomainService.getDomain(site.id);
        
        // ドメイン設定が存在し、isActiveがtrueの場合のみトラッキング
        if (domainConfig && domainConfig.isActive) {
          const firstPageId = pages[0].pageId; // 最初のページのpageIdを動的に取得
          AnalyticsTrackingService.startSession(site.id, firstPageId);
        }
      } catch (error) {
        console.error('[SwipeableView] Error checking domain configuration:', error);
        // エラーが発生した場合はトラッキングをスキップ
      }
    };

    initializeTracking();
  }, [site?.id, pages, isPreviewMode]);

  // ページビューのトラッキング
  useEffect(() => {
    const trackPageView = async () => {
      // プレビューモードの場合はトラッキングをスキップ
      const hostname = window.location.hostname;
      const isPreviewByHostname = hostname === 'preview.qwipe.jp' || hostname === 'www.preview.qwipe.jp';
      
      if (isPreviewMode || isPreviewByHostname) {
        return;
      }

      if (!site?.id || !currentPage?.pageId) {
        return;
      }

      // 有効なドメイン設定があるかチェック
      try {
        const domainConfig = await DomainService.getDomain(site.id);
        
        // ドメイン設定が存在し、isActiveがtrueの場合のみトラッキング
        if (domainConfig && domainConfig.isActive) {
          AnalyticsTrackingService.trackPageView(site.id, currentPage.pageId);
        }
      } catch (error) {
        console.error('[SwipeableView] Error checking domain configuration for page view:', error);
        // エラーが発生した場合はトラッキングをスキップ
      }
    };

    trackPageView();
  }, [site?.id, currentPage?.pageId, isPreviewMode]);


  // デモモード自動スワイプ
  useEffect(() => {
    // デモモードがOFFまたはプレビューモードでない場合は何もしない
    if (!demoMode || !isPreviewMode) {
      if (demoTimerRef.current) {
        clearTimeout(demoTimerRef.current);
        demoTimerRef.current = null;
      }
      return;
    }

    // 15秒後に自動スワイプ
    demoTimerRef.current = setTimeout(() => {
      const currentPageIndex = pages.findIndex(p => p.id === currentPageId);
      
      // 部分スワイプがあるかチェック
      let hasPartialSwipe = false;
      let isLastPartialSlide = false;
      let useHorizontalSlides = false;
      
      if (currentPage) {
        // モック用の部分スワイプ（subPageIds）
        if (currentPage.isHorizontal && currentPage.subPageIds && currentPage.subPageIds.length > 1 && !currentPage.horizontalSlides) {
          hasPartialSwipe = true;
          const totalSlides = currentPage.subPageIds.length;
          const normalizedIndex = ((horizontalPageIndex % totalSlides) + totalSlides) % totalSlides;
          isLastPartialSlide = normalizedIndex === totalSlides - 1;
          useHorizontalSlides = false;
        }
        // 実際のサイトの部分スワイプ（horizontalSlides）
        else if (currentPage.horizontalSlides && currentPage.horizontalSlides.length > 0) {
          hasPartialSwipe = true;
          useHorizontalSlides = true;
          const partialState = getGlobalSlideState(currentPage.id);
          const totalSlides = currentPage.horizontalSlides.length;
          isLastPartialSlide = partialState.currentSlideIndex === totalSlides - 1;
          console.log('[SwipeableView] Partial swipe state:', { 
            pageId: currentPage.id, 
            currentSlideIndex: partialState.currentSlideIndex, 
            totalSlides, 
            isLastPartialSlide 
          });
        }
      }
      
      // 部分スワイプがある場合
      if (hasPartialSwipe && !isLastPartialSlide) {
        // まだ部分スワイプ内に次のスライドがある場合
        if (useHorizontalSlides && currentPage) {
          // PartialHorizontalSlideコンポーネントにイベントを送信
          console.log('[SwipeableView] Dispatching partialSlideNext event for pageId:', currentPage.id);
          window.dispatchEvent(new CustomEvent('partialSlideNext', {
            detail: { pageId: currentPage.id }
          }));
          // イベント送信後、デモタイマーを再起動するためにトリガーを更新
          setTimeout(() => {
            setDemoTrigger(prev => prev + 1);
          }, 100);
        } else {
          // モック用の部分スワイプは右にスワイプ
          goToNextColumn();
        }
        return;
      }
      
      // 部分スワイプの最後、または部分スワイプがない場合は次のメインページへ
      // 優先順位: 下 > 右（隣接ページ） > 配列の次のページ
      if (currentPage && getAdjacentPages[currentPage.id]?.down) {
        goToNextRow();
      } else if (currentPage && getAdjacentPages[currentPage.id]?.right) {
        goToNextColumn();
      } else {
        // 次のメインページに移動（配列順）
        const nextPageIndex = currentPageIndex + 1;
        if (nextPageIndex < pages.length) {
          setCurrentPageId(pages[nextPageIndex].id);
        } else {
          // 配列の最後に到達したら最初に戻る
          setCurrentPageId(pages[0]?.id || '');
        }
      }
    }, 15000);

    return () => {
      if (demoTimerRef.current) {
        clearTimeout(demoTimerRef.current);
      }
    };
  }, [demoMode, isPreviewMode, currentPageId, currentPage, getAdjacentPages, goToNextRow, goToNextColumn, goToPreviousRow, goToPreviousColumn, pages, horizontalPageIndex, demoTrigger]);

  // CTAボタンのクリックハンドラ（クリックされたボタンを渡すとそのlinkUrlを使用）
  const handleCTAClick = async (clickedButton?: any) => {
    const hostname = window.location.hostname;
    const isPreviewByHostname = hostname === 'preview.qwipe.jp' || hostname === 'www.preview.qwipe.jp';
    
    if (!isPreviewMode && !isPreviewByHostname && site?.id) {
      try {
        const domainConfig = await DomainService.getDomain(site.id);
        if (domainConfig && domainConfig.isActive) {
          AnalyticsTrackingService.trackCTAClick(site.id, currentPage?.pageId);
        }
      } catch (error) {
        console.error('[SwipeableView] Error checking domain configuration for CTA click:', error);
      }
    }

    let targetButton: any = clickedButton ?? null;
    if (!targetButton && site?.settings?.ctaButtons && currentPage?.ctaButtonId) {
      targetButton = site.settings.ctaButtons.find((btn: any) => btn.id === currentPage.ctaButtonId);
    }
    if (!targetButton && site?.settings?.ctaButtons && currentPage?.ctaButtonIds?.length) {
      targetButton = site.settings.ctaButtons.find((btn: any) => btn.id === currentPage.ctaButtonIds![0]);
    }

    if (targetButton?.linkUrl) {
      if (targetButton.openInNewTab) {
        window.open(targetButton.linkUrl, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = targetButton.linkUrl;
      }
    }
  };

  // pagesが空またはundefinedの場合は何も表示しない（ちらつき防止）
  // ただし、onReadyは呼び出す（親要素のローディングを解除するため）
  useEffect(() => {
    if (!pages || pages.length === 0) {
      console.warn('SwipeableView: pagesが空');
      if (onReady) {
        console.warn('SwipeableView: ページが存在しないため準備完了通知');
        onReady();
      }
      return;
    }
    
    if (!currentPage) {
      console.warn('SwipeableView: currentPageが存在しない', { 
        pagesLength: pages.length, 
        currentPageId,
        firstPageId: pages[0]?.id 
      });
      if (onReady) {
        console.warn('SwipeableView: currentPageが存在しないため準備完了通知');
        onReady();
      }
      return;
    }
  }, [pages, currentPage, onReady, currentPageId]);

  if (!pages || pages.length === 0 || !currentPage) {
    return null;
  }

  // 1ページ目の背景画像が読み込まれるまで待つ
  if (!isFirstPageImageReady) {
    return null;
  }

  return (
    <div 
      {...handlers} 
      className={`swipeable-view ${className}`} 
      style={{ 
        touchAction: 'pan-y', // 縦スクロールは許可、横スワイプを検出
        height: '100dvh', 
        width: '100vw',
        cursor: 'grab',
        backgroundColor: site?.settings?.backgroundColor || undefined,
        backgroundImage: site?.settings?.backgroundImage ? `url(${site.settings.backgroundImage})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* メインコンテンツ */}
      <div className="qwipe-main-container">
        {/* 中央の描画範囲 - 3D空間でのページ配置 */}
        <div className="qwipe-viewport">
          <div className="pages-3d-container" style={{
            transform: swipeDirection === 'up'
              ? `translateY(${-100 + (swipeProgress * 100)}%)`
              : swipeDirection === 'down'
              ? `translateY(${100 - (swipeProgress * 100)}%)`
              : swipeDirection === 'left'
              ? `translateX(${-100 + (swipeProgress * 100)}%)`
              : swipeDirection === 'right'
              ? `translateX(${100 - (swipeProgress * 100)}%)`
              : `translateX(-${(getColumnNumber(currentPage?.column || 'A') - 1) * 100}%) translateY(-${((currentPage?.row || 1) - 1) * 100}%)`
          }}>
            {/* すべてのページを事前レンダリング（ちらつき防止） */}
            {pages.map((page, index) => {
              const displayPage = getCurrentPageWithAdjacents.current || currentPage;
              const isCurrentPage = displayPage?.id === page.id;
              
              const currentPageForAdjacent = displayPage || currentPage;
              if (!currentPageForAdjacent) return null;
              
              // 現在のページまたは隣接ページのみ表示
              const isAdjacent = (
                (page.row === currentPageForAdjacent.row - 1 && page.column === 'A') || // 上
                (page.row === currentPageForAdjacent.row + 1 && page.column === 'A') || // 下
                (page.row === currentPageForAdjacent.row && 
                 (getColumnNumber(page.column) === getColumnNumber(currentPageForAdjacent.column) - 1 || // 左
                  getColumnNumber(page.column) === getColumnNumber(currentPageForAdjacent.column) + 1)) // 右
              );
              
              if (!isCurrentPage && !isAdjacent) return null;
              
              // ページの背景色を取得（サイト設定から取得、未設定の場合はデフォルト値#ffffff）
              const pageBgColor = site?.settings?.pageBackgroundColor || '#ffffff';
              
              // スタイルオブジェクトを構築（背景色を確実に適用）
              const containerStyle: React.CSSProperties = {
                left: `${(getColumnNumber(page.column) - 1) * 100}%`,
                top: `${(page.row - 1) * 100}%`,
                zIndex: isCurrentPage ? 10 : 5,
                width: '100%',
                height: '100%',
                willChange: 'opacity', // GPUアクセラレーション
                pointerEvents: isCurrentPage ? 'auto' : 'none', // 現在のページのみタッチイベントを受け付ける
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundColor: pageBgColor, // 常に背景色を設定（デフォルトは#ffffff）
              };
              
              // 背景画像を設定（ある場合）
              // 現在のページまたは隣接ページの場合は即座に読み込み、それ以外は遅延読み込み
              if (page.backgroundImage) {
                const backgroundImageUrl = page.backgroundImage; // 型を明確にする
                const isPriorityPage = isCurrentPage || isAdjacent;
                if (isPriorityPage) {
                  // 優先ページの画像は即座に読み込む
                  containerStyle.backgroundImage = `url(${backgroundImageUrl})`;
                  // 画像をプリロード（まだ読み込まれていない場合）
                  if (!imagesLoaded.has(backgroundImageUrl)) {
                    const img = new Image();
                    // fetchPriorityは型定義にないが、ブラウザでサポートされている
                    (img as any).fetchPriority = isCurrentPage ? 'high' : 'auto';
                    img.src = backgroundImageUrl;
                    setImagesLoaded(prev => new Set([...Array.from(prev), backgroundImageUrl]));
                  }
                } else {
                  // 非優先ページの画像は遅延読み込み（Intersection Observerを使用）
                  // ただし、背景画像URLは設定しておく（ブラウザの遅延読み込み機能を使用）
                  containerStyle.backgroundImage = `url(${backgroundImageUrl})`;
                  // 画像を遅延読み込み
                  if (!imagesLoaded.has(backgroundImageUrl)) {
                    const img = new Image();
                    img.loading = 'lazy';
                    img.src = backgroundImageUrl;
                    img.onload = () => {
                      setImagesLoaded(prev => new Set([...Array.from(prev), backgroundImageUrl]));
                    };
                  }
                }
              }
              
              return (
                <motion.div
                  key={page.id}
                  className="qwipe-page-container"
                  style={containerStyle}
                  initial={false} // 初期アニメーション無効化
                  animate={{
                    opacity: isCurrentPage ? 1 : 0.3
                  }}
                  transition={{
                    opacity: { duration: 0.3, ease: "easeInOut" }
                  }}
                  onError={(e) => {
                    console.error('Background image failed to load:', page.backgroundImage);
                    e.currentTarget.style.backgroundImage = 'none';
                  }}
                >
                  <PageContent 
                    page={page} 
                    horizontalPageIndex={horizontalPageIndex}
                    isCurrentPage={isCurrentPage}
                    site={site}
                    isFirstPage={index === 0} // 最初のページかどうか
                    onEmbeddedCtaClick={handleCTAClick}
                  />
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* ページ番号表示（Flutterアプリと同じ位置） */}
      {site?.settings?.showPageCount !== false && (
        <div className="qwipe-page-indicator">
          <div className="page-number-display">
            {currentPage?.row}-{currentPage?.column}
          </div>
        </div>
      )}
      
      
      
      {/* 会社概要ウィジェット（設定されている場合のみ） */}
      <AnimatePresence>
        {site?.settings?.companyInfo && 
         (site.settings.companyInfo.companyName || site.settings.companyInfo.description || 
          site.settings.companyInfo.address || site.settings.companyInfo.website) &&
         currentPage?.companyInfoVisible !== false && (
          <motion.div
            key="company-info"
            className="qwipe-company-info"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <div className="company-info-band" onClick={() => setShowCompanyModal(true)}>
              会社概要
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* PC用D-padコントロール（1025px以上） */}
      <div className="qwipe-dpad-controls">
        <DPadControls 
          onUp={goToPreviousRow}
          onDown={goToNextRow}
          onLeft={goToPreviousColumn}
          onRight={goToNextColumn}
          canSwipeUp={Boolean(currentPage && getAdjacentPages[currentPage.id]?.up)}
          canSwipeDown={Boolean(currentPage && getAdjacentPages[currentPage.id]?.down)}
          canSwipeLeft={Boolean(
            currentPage && (
              (currentPage.isHorizontal && currentPage.subPageIds && currentPage.subPageIds.length > 1 && !currentPage.horizontalSlides) ||
              (getAdjacentPages[currentPage.id]?.left && currentPage.column !== 'A')
            )
          )}
          canSwipeRight={Boolean(
            currentPage && (
              (currentPage.isHorizontal && currentPage.subPageIds && currentPage.subPageIds.length > 1 && !currentPage.horizontalSlides) ||
              getAdjacentPages[currentPage.id]?.right
            )
          )}
        />
      </div>
      
      {/* グローバルCTAボタン（固定位置表示、複数対応） */}
      {(() => {
        if (!site?.settings?.ctaButtons || currentPage?.ctaButtonVisible === false) {
          return null;
        }
        
        // 選択されたボタンの配列を取得
        let selectedButtons: any[] = [];
        
        // 新しい方式（ctaButtonIds）が設定されている場合
        if (currentPage?.ctaButtonIds && currentPage.ctaButtonIds.length > 0) {
          selectedButtons = site.settings.ctaButtons.filter(btn => 
            currentPage.ctaButtonIds!.includes(btn.id)
          );
        }
        // 後方互換性：ctaButtonIdが設定されている場合
        else if (currentPage?.ctaButtonId) {
          const button = site.settings.ctaButtons.find(btn => btn.id === currentPage.ctaButtonId);
          if (button) {
            selectedButtons = [button];
          }
        }
        
        // 固定表示モード（displayMode === 'fixed'）のボタンのみをフィルタリング
        // 埋め込み式（displayMode === 'embedded'）のボタンは、HTML/CSS設定内で直接配置されるため、ここでは表示しない
        const fixedButtons = selectedButtons.filter(btn => !btn.displayMode || btn.displayMode === 'fixed');
        
        // 複数のボタンを表示
        return fixedButtons.map((selectedButton, index) => (
          <FixedCTAButton 
            key={`cta-${selectedButton.id || index}`}
            image={selectedButton.image}
            hoverImage={selectedButton.hoverImage}
            bottomPercent={selectedButton.bottomPercent}
            leftPosition={selectedButton.leftPosition}
            leftPercentCustom={selectedButton.leftPercentCustom}
            leftPercent={selectedButton.leftPercent}
            widthPercent={selectedButton.widthPercent ?? 80}
            animationType={selectedButton.animationType || 'none'}
            onClick={() => handleCTAClick(selectedButton)}
          />
        ));
      })()}
      
      {/* 会社概要モーダル */}
      {showCompanyModal && (
        <CompanyModal site={site} onClose={() => setShowCompanyModal(false)} />
      )}
      
      {/* デモモード切替ボタン（プレビューモード時のみ） */}
      {isPreviewMode && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 10000,
          display: 'flex',
          gap: '10px',
          alignItems: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          padding: '12px 16px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
        }}>
          <span style={{
            color: 'white',
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            デモモード
          </span>
          <button
            onClick={() => setDemoMode(!demoMode)}
            style={{
              padding: '8px 16px',
              backgroundColor: demoMode ? '#4caf50' : '#9e9e9e',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.8';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            {demoMode ? 'ON' : 'OFF'}
          </button>
        </div>
      )}
    </div>
  );
};

